import React, { useState, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { UserContext } from '../userContext';
import { 
  collection, query, where, getDocs, doc, updateDoc, arrayUnion,
  orderBy, Timestamp
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { 
  FaBell, FaTimes, FaVideo, FaFile, FaImage, FaLink,
  FaCalendarAlt, FaEye, FaSpinner
} from 'react-icons/fa';
import './Notificacoes.css';

function Notificacoes({ onClose }) {
  const { user } = useContext(UserContext);
  const [avisos, setAvisos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAsRead, setMarkingAsRead] = useState(new Set());

  useEffect(() => {
    if (user?.uid) {
      carregarAvisos();
    }
  }, [user]);

  const carregarAvisos = async () => {
    if (!user?.uid) return;

    try {
      setLoading(true);
      const agora = Timestamp.now();
      
      // Buscar avisos ativos no período atual
      const q = query(
        collection(db, 'avisos'),
        where('ativo', '==', true),
        where('dataInicio', '<=', agora),
        where('dataFim', '>=', agora),
        orderBy('dataInicio', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const avisosData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        dataInicio: doc.data().dataInicio?.toDate?.() || new Date(),
        dataFim: doc.data().dataFim?.toDate?.() || new Date(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));

      // Filtrar avisos que o usuário já visualizou (se não for popup)
      const avisosComStatus = avisosData.map(aviso => ({
        ...aviso,
        visualizado: aviso.visualizacoes?.includes(user.uid) || false
      }));

      setAvisos(avisosComStatus);
    } catch (error) {
      console.error('Erro ao carregar avisos:', error);
    } finally {
      setLoading(false);
    }
  };

  const marcarComoLido = async (avisoId) => {
    if (!user?.uid || markingAsRead.has(avisoId)) return;

    try {
      setMarkingAsRead(prev => new Set([...prev, avisoId]));
      
      // Atualizar no Firebase
      await updateDoc(doc(db, 'avisos', avisoId), {
        visualizacoes: arrayUnion(user.uid)
      });

      // Atualizar estado local
      setAvisos(prev => prev.map(aviso => 
        aviso.id === avisoId 
          ? { ...aviso, visualizado: true }
          : aviso
      ));
    } catch (error) {
      console.error('Erro ao marcar como lido:', error);
    } finally {
      setMarkingAsRead(prev => {
        const newSet = new Set(prev);
        newSet.delete(avisoId);
        return newSet;
      });
    }
  };

  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 'video':
      case 'video_texto':
        return <FaVideo className="tipo-icon video" />;
      case 'texto_imagem':
        return <FaImage className="tipo-icon imagem" />;
      case 'texto_link':
        return <FaLink className="tipo-icon link" />;
      default:
        return <FaFile className="tipo-icon texto" />;
    }
  };

  const getGoogleDriveFileId = (url) => {
    if (!url) return null;

    try {
      const parsed = new URL(url);
      const host = parsed.hostname.replace(/^www\./, '');
      if (host !== 'drive.google.com') return null;

      const pathParts = parsed.pathname.split('/').filter(Boolean);
      const fileIndex = pathParts.findIndex((part) => part === 'd');
      if (fileIndex > -1 && pathParts[fileIndex + 1]) {
        return pathParts[fileIndex + 1];
      }

      return parsed.searchParams.get('id');
    } catch (_) {
      return null;
    }
  };

  const normalizeImageUrl = (url) => {
    const cleanUrl = (url || '').trim();
    const driveId = getGoogleDriveFileId(cleanUrl);
    if (!driveId) return cleanUrl;
    return `https://drive.google.com/thumbnail?id=${driveId}&sz=w2000`;
  };

  const getImageFallbackUrl = (url) => {
    const driveId = getGoogleDriveFileId((url || '').trim());
    if (!driveId) return '';
    return `https://drive.google.com/uc?export=view&id=${driveId}`;
  };

  const normalizeVideoUrl = (url) => {
    const cleanUrl = (url || '').trim();
    const driveId = getGoogleDriveFileId(cleanUrl);
    if (!driveId) return cleanUrl;
    return `https://drive.google.com/uc?export=download&id=${driveId}`;
  };

  const renderConteudoAviso = (aviso) => {
    const { tipo, conteudo, imagemUrl, imagemURL, imageUrl, linkUrl, videoUrl } = aviso;
    const imagemOriginal = (imagemUrl || imagemURL || imageUrl || '').trim();
    const imagemAviso = normalizeImageUrl(imagemOriginal);
    const imagemFallback = getImageFallbackUrl(imagemOriginal);
    const videoMediaUrl = normalizeVideoUrl(videoUrl);
    const isYouTubeUrl = !!videoMediaUrl && (
      videoMediaUrl.includes('youtube.com') ||
      videoMediaUrl.includes('youtu.be') ||
      videoMediaUrl.includes('youtube-nocookie.com')
    );
    const embedUrl = isYouTubeUrl ? getYouTubeEmbedUrl(videoMediaUrl) : null;

    return (
      <div className="aviso-conteudo-completo">
        {!!conteudo && <p className="aviso-texto">{conteudo}</p>}
        
        {imagemAviso && (
          <div className="aviso-media">
            <img 
              src={imagemAviso} 
              alt="Imagem do aviso"
              className="aviso-imagem"
              onError={(e) => {
                if (imagemFallback && e.currentTarget.src !== imagemFallback) {
                  e.currentTarget.src = imagemFallback;
                  return;
                }
                e.currentTarget.style.display = 'none';
              }}
            />
            <a
              href={imagemOriginal || imagemAviso}
              target="_blank"
              rel="noopener noreferrer"
              className="aviso-link"
              style={{ marginTop: '0.75rem' }}
            >
              <FaLink />
              Abrir imagem
            </a>
          </div>
        )}
        
        {tipo === 'texto_link' && linkUrl && (
          <div className="aviso-media">
            <a 
              href={linkUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="aviso-link"
            >
              <FaLink />
              Acessar Link
            </a>
          </div>
        )}
        
        {(tipo === 'video' || tipo === 'video_texto') && videoMediaUrl && (
          <div className="aviso-media">
            {embedUrl ? (
              <div className="video-wrapper">
                <iframe
                  src={embedUrl}
                  title="Vídeo do aviso"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="aviso-video"
                />
              </div>
            ) : isYouTubeUrl ? (
              <a
                href={videoMediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="aviso-link"
              >
                <FaLink />
                Abrir vídeo no YouTube
              </a>
            ) : (
              <video controls className="aviso-video">
                <source src={videoMediaUrl} type="video/mp4" />
                Seu navegador não suporta o elemento de vídeo.
              </video>
            )}
          </div>
        )}
      </div>
    );
  };

  const getYouTubeEmbedUrl = (url) => {
    const videoId = getYouTubeVideoId(url);
    return videoId ? `https://www.youtube-nocookie.com/embed/${videoId}` : null;
  };

  const getYouTubeVideoId = (url) => {
    if (!url) return null;

    try {
      const parsed = new URL(url);
      const host = parsed.hostname.replace(/^www\./, '');

      if (host === 'youtu.be') {
        const id = parsed.pathname.split('/').filter(Boolean)[0];
        return id?.length === 11 ? id : null;
      }

      if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
        const watchId = parsed.searchParams.get('v');
        if (watchId?.length === 11) return watchId;

        const pathParts = parsed.pathname.split('/').filter(Boolean);
        const embedIndex = pathParts.findIndex((part) => part === 'embed' || part === 'shorts' || part === 'live');
        if (embedIndex >= 0 && pathParts[embedIndex + 1]?.length === 11) {
          return pathParts[embedIndex + 1];
        }
      }
    } catch (_) {
      return null;
    }

    return null;
  };

  const avisosNaoLidos = avisos.filter(aviso => !aviso.visualizado);

  if (typeof document === 'undefined' || !document.body) {
    return null;
  }

  return createPortal(
    <div className="notificacoes-overlay" onClick={onClose}>
      <div className="notificacoes-modal" onClick={e => e.stopPropagation()}>
        <div className="notificacoes-header">
          <div className="header-info">
            <FaBell className="header-icon" />
            <h3>Notificações</h3>
            {avisosNaoLidos.length > 0 && (
              <span className="contador-notif">{avisosNaoLidos.length}</span>
            )}
          </div>
          <button onClick={onClose} className="close-button">
            <FaTimes />
          </button>
        </div>

        <div className="notificacoes-content">
          {loading ? (
            <div className="loading-state">
              <FaSpinner className="spinner" />
              <p>Carregando notificações...</p>
            </div>
          ) : avisos.length === 0 ? (
            <div className="empty-state">
              <FaBell className="empty-icon" />
              <h4>Nenhuma notificação</h4>
              <p>Você não possui notificações no momento.</p>
            </div>
          ) : (
            <div className="avisos-list">
              {avisos.map(aviso => (
                <div 
                  key={aviso.id} 
                  className={`aviso-item ${aviso.visualizado ? 'lido' : 'nao-lido'}`}
                >
                  <div className="aviso-header">
                    <div className="aviso-header-left">
                      {getTipoIcon(aviso.tipo)}
                      <div className="aviso-titulo-data">
                        <h4 className="aviso-titulo">{aviso.titulo}</h4>
                        <div className="aviso-data">
                          <FaCalendarAlt />
                          <span>
                            {aviso.createdAt.toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {!aviso.visualizado && (
                      <button
                        onClick={() => marcarComoLido(aviso.id)}
                        className="marcar-lido-btn"
                        disabled={markingAsRead.has(aviso.id)}
                        title="Marcar como lido"
                      >
                        {markingAsRead.has(aviso.id) ? (
                          <FaSpinner className="spinner-small" />
                        ) : (
                          <FaEye />
                        )}
                      </button>
                    )}
                  </div>

                  {renderConteudoAviso(aviso)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default Notificacoes;
